import React, { useMemo } from 'react';
import { formatRupiah } from '../data/financialData';
import type { Area } from '../data/financialData';
import { Bar } from 'react-chartjs-2';
import { useTheme } from '../ThemeContext';
import { useData } from '../DataContext';
import { useSmartInsights } from '../SmartInsightsContext';
import SmartTooltip from './SmartTooltip';
import { analyzeKPI, analyzeChartTrend } from '../utils/smartInsightsEngine';

interface ExecutiveSummaryPageProps {
  area: Area;
  branch: string;
  startIdx: number;
  endIdx: number;
  onNavigate: (section: string) => void;
  activeEntity?: string;
}

/* ─── Helpers ─────────────────────────────────────────────────── */

function getTrafficLight(current: number, previous: number): { color: string; label: string } {
  if (current > 0 && current >= previous) return { color: '#10b981', label: 'Positif & Growing' };
  if (current > 0 && current < previous) return { color: '#f59e0b', label: 'Positif tapi Turun' };
  return { color: '#ef4444', label: 'Negatif' };
}

function clamp(v: number, min: number, max: number) {
  return Math.max(min, Math.min(max, v));
}

/* ─── Component ───────────────────────────────────────────────── */

const ExecutiveSummaryPage: React.FC<ExecutiveSummaryPageProps> = ({
  area,
  branch,
  startIdx,
  endIdx,
  onNavigate,
  activeEntity,
}) => {
  const { chart: chartTheme } = useTheme();
  const { MONTHS, getKPIs, profitLoss, profitLossByBranch, branches, balanceSheet, branchToArea } = useData();
  const effectiveKey = branch === 'ALL' ? (area as string) : branch;

  const { isEnabled: insightsEnabled } = useSmartInsights();

  // Get PL data
  const pl = useMemo(() => {
    return (profitLossByBranch as any)[effectiveKey]
      ?? (profitLoss as any)[effectiveKey]
      ?? profitLoss['ALL'];
  }, [effectiveKey]);

  const kpis = useMemo(() => getKPIs(effectiveKey, startIdx, endIdx), [effectiveKey, startIdx, endIdx]);

  // Previous period KPIs for comparison
  const prevKpis = useMemo(() => {
    const length = endIdx - startIdx + 1;
    const prevStart = Math.max(0, startIdx - length);
    const prevEnd = startIdx - 1;
    if (prevEnd < 0) return null;
    return getKPIs(effectiveKey, prevStart, prevEnd);
  }, [effectiveKey, startIdx, endIdx]);

  // Sparkline data: last 6 months
  const sparklineRange = useMemo(() => {
    const start = Math.max(0, endIdx - 5);
    return { start, end: endIdx };
  }, [endIdx]);

  const sparklineData = useMemo(() => {
    const s = sparklineRange.start;
    const e = sparklineRange.end;
    return {
      revenue: pl.revenue.total.slice(s, e + 1),
      cogs: pl.cogs.total.slice(s, e + 1),
      grossProfit: pl.grossProfit.slice(s, e + 1),
      opex: pl.opex.total.slice(s, e + 1),
      netProfit: pl.netProfit.slice(s, e + 1),
      labels: MONTHS.slice(s, e + 1).map((m: any) => m.label),
    };
  }, [pl, sparklineRange]);

  // Top 5 branches by revenue
  const topBranches = useMemo(() => {
    const results: { code: string; name: string; revenue: number }[] = [];
    for (const b of branches) {
      if (b.status === 'Closed') continue; // Filter out closed branches
      const bpl = profitLossByBranch[b.code];
      if (!bpl) continue;
      const bArea = (branchToArea as any)[b.code];
      if (area !== 'ALL' && bArea !== area) continue;
      let total = 0;
      for (let i = startIdx; i <= endIdx; i++) {
        total += bpl.revenue.total[i] || 0;
      }
      if (total > 0) results.push({ code: b.code, name: b.name, revenue: total });
    }
    return results.sort((a, b) => b.revenue - a.revenue).slice(0, 5);
  }, [area, startIdx, endIdx]);

  // Top 5 underperforming branches by net profit (active branches with revenue only)
  const underperformingBranches = useMemo(() => {
    const results: { code: string; name: string; netProfit: number }[] = [];
    for (const b of branches) {
      if (b.status === 'Closed') continue; // Filter out closed branches
      const bpl = profitLossByBranch[b.code];
      if (!bpl) continue;
      const bArea = (branchToArea as any)[b.code];
      if (area !== 'ALL' && bArea !== area) continue;
      
      // Filter out branches with no revenue in the selected period
      let totalRev = 0;
      for (let i = startIdx; i <= endIdx; i++) {
        totalRev += bpl.revenue.total[i] || 0;
      }
      if (totalRev === 0) continue;

      let totalNet = 0;
      for (let i = startIdx; i <= endIdx; i++) {
        totalNet += bpl.netProfit[i] || 0;
      }
      results.push({ code: b.code, name: b.name, netProfit: totalNet });
    }
    return results.sort((a, b) => a.netProfit - b.netProfit).slice(0, 5);
  }, [area, startIdx, endIdx]);

  const maxUnderperformLoss = useMemo(() => {
    const absoluteLosses = underperformingBranches.map(b => Math.abs(b.netProfit));
    return Math.max(...absoluteLosses, 1);
  }, [underperformingBranches]);

  // Financial Health Score (0-100)
  const healthMetrics = useMemo(() => {
    const bs = (balanceSheet as any)[area === 'ALL' ? 'ALL' : area];
    const currentAssets = bs?.currentAssets?.total?.[endIdx] ?? 0;
    const currentLiab = Math.abs(bs?.currentLiabilities?.total?.[endIdx] ?? 1);
    const currentRatio = currentLiab > 0 ? currentAssets / currentLiab : 0;
    const currentRatioScore = clamp(currentRatio * 25, 0, 100);

    const grossMargin = kpis.totalRevenue > 0 ? (kpis.grossProfit / kpis.totalRevenue) * 100 : 0;
    const marginScore = clamp(grossMargin, 0, 100);

    const revGrowth = prevKpis && prevKpis.totalRevenue > 0
      ? ((kpis.totalRevenue - prevKpis.totalRevenue) / Math.abs(prevKpis.totalRevenue)) * 100
      : 0;
    const growthScore = clamp(50 + revGrowth, 0, 100);

    const profitScore = kpis.netProfit >= 0 ? 80 : clamp(50 + (kpis.netProfit / 1e6), 0, 50);

    const overall = Math.round((currentRatioScore * 0.2 + marginScore * 0.3 + growthScore * 0.25 + profitScore * 0.25));

    return {
      currentRatio,
      currentRatioScore: Math.round(currentRatioScore),
      grossMargin,
      marginScore: Math.round(marginScore),
      revGrowth,
      growthScore: Math.round(growthScore),
      profitScore: Math.round(profitScore),
      overall: clamp(overall, 0, 100),
    };
  }, [area, endIdx, kpis, prevKpis]);

  // Traffic lights for net profit
  const netProfitCurrent = pl.netProfit[endIdx] || 0;
  const netProfitPrev = endIdx > 0 ? (pl.netProfit[endIdx - 1] || 0) : 0;
  const npTraffic = getTrafficLight(netProfitCurrent, netProfitPrev);

  // KPI items
  const kpiItems = [
    { title: 'Total Revenue', value: kpis.totalRevenue, data: sparklineData.revenue, color: '#10b981', prev: prevKpis?.totalRevenue },
    { title: 'COGS', value: kpis.cogs, data: sparklineData.cogs, color: '#f59e0b', prev: prevKpis?.cogs },
    { title: 'Gross Profit', value: kpis.grossProfit, data: sparklineData.grossProfit, color: '#3b82f6', prev: prevKpis?.grossProfit },
    { title: 'Operating Expenses', value: kpis.opex, data: sparklineData.opex, color: '#ef4444', prev: prevKpis?.opex },
    { title: 'Net Profit', value: kpis.netProfit, data: sparklineData.netProfit, color: activeEntity === 'JUARA_GADAI' ? '#F68B1F' : '#8b5cf6', prev: prevKpis?.netProfit },
    { title: 'Cash from Ops', value: kpis.netCashFromOps, data: sparklineData.revenue, color: '#06b6d4', prev: prevKpis?.netCashFromOps },
  ];

  const pct = (curr: number, prev: number | undefined) =>
    prev === undefined || prev === 0 ? 0 : ((curr - prev) / Math.abs(prev)) * 100;

  // Bar chart data for Revenue vs OpEx
  const barChartData = useMemo(() => ({
    labels: sparklineData.labels,
    datasets: [
      {
        label: 'Revenue',
        data: sparklineData.revenue,
        backgroundColor: 'rgba(16, 185, 129, 0.7)',
        borderColor: '#10b981',
        borderWidth: 1,
        borderRadius: 4,
      },
      {
        label: 'OpEx',
        data: sparklineData.opex,
        backgroundColor: 'rgba(239, 68, 68, 0.7)',
        borderColor: '#ef4444',
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  }), [sparklineData]);

  const healthColor = healthMetrics.overall >= 70 ? '#10b981' : healthMetrics.overall >= 40 ? '#f59e0b' : '#ef4444';
  const circumference = 2 * Math.PI * 56;
  const dashOffset = circumference - (circumference * healthMetrics.overall / 100);

  const maxBranchRev = topBranches.length > 0 ? topBranches[0].revenue : 1;

  return (
    <div className="animate-in">
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '22px', fontWeight: '800', color: 'var(--text-primary)', letterSpacing: '-0.5px', marginBottom: '4px' }}>
          🎯 Executive Summary
        </h2>
        <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
          Ringkasan keuangan periode {MONTHS[startIdx]?.label} – {MONTHS[endIdx]?.label}
          {area !== 'ALL' ? ` • Area ${area}` : ''}
          {branch !== 'ALL' ? ` • ${branch}` : ''}
        </p>
      </div>

      {/* 6 KPI Cards with Sparklines */}
      <div className="exec-grid">
        {kpiItems.map((item, idx) => {
          const change = pct(item.value, item.prev);
          const isPositive = change >= 0;
          const maxVal = Math.max(...item.data.map(Math.abs), 1);

          const itemInsights = insightsEnabled ? analyzeKPI({
            title: item.title,
            value: item.value,
            prevValue: item.prev,
            kpis: kpis,
            prevKpis: prevKpis
          }) : [];

          return (
            <SmartTooltip key={idx} insights={itemInsights} title={item.title}>
              <div className="exec-kpi-card animate-in" style={{ animationDelay: `${idx * 0.06}s` }}>
                <div className="exec-kpi-header">
                  <span className="exec-kpi-title">{item.title}</span>
                  {item.title === 'Net Profit' && (
                    <span className="exec-traffic-light" style={{ backgroundColor: npTraffic.color, color: npTraffic.color }} title={npTraffic.label} />
                  )}
                </div>
                <div className="exec-kpi-value">{formatRupiah(item.value)}</div>
                {item.prev !== undefined && (
                  <span style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    backgroundColor: isPositive ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
                    color: isPositive ? '#34d399' : '#f87171',
                  }}>
                    {isPositive ? '▲' : '▼'} {Math.abs(change).toFixed(1)}%
                  </span>
                )}
                <div className="exec-kpi-sparkline" style={{ marginTop: '10px' }}>
                  {item.data.map((val: number, i: number) => (
                    <div
                      key={i}
                      className="exec-kpi-sparkline-bar"
                      style={{
                        height: `${Math.max(8, (Math.abs(val) / maxVal) * 100)}%`,
                        backgroundColor: val >= 0 ? item.color : '#ef4444',
                        opacity: 0.4 + (i / item.data.length) * 0.6,
                      }}
                    />
                  ))}
                </div>
              </div>
            </SmartTooltip>
          );
        })}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
        {/* Financial Health Score */}
        <div className="exec-section">
          <div className="exec-section-title">💪 Financial Health Score</div>
          <div className="exec-health-score">
            <div className="exec-health-ring">
              <svg width="140" height="140" viewBox="0 0 140 140">
                <circle cx="70" cy="70" r="56" fill="none" stroke="var(--border-color)" strokeWidth="10" />
                <circle
                  cx="70" cy="70" r="56"
                  fill="none"
                  stroke={healthColor}
                  strokeWidth="10"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={dashOffset}
                  transform="rotate(-90 70 70)"
                  style={{ transition: 'stroke-dashoffset 1s cubic-bezier(0.4, 0, 0.2, 1)' }}
                />
              </svg>
              <div className="exec-health-label">
                <div className="exec-health-value" style={{ color: healthColor }}>{healthMetrics.overall}</div>
                <div className="exec-health-text">/ 100</div>
              </div>
            </div>

            <div className="exec-health-factors">
              {[
                { label: 'Current Ratio', score: healthMetrics.currentRatioScore, detail: `${healthMetrics.currentRatio.toFixed(2)}x`, color: '#3b82f6' },
                { label: 'Gross Margin', score: healthMetrics.marginScore, detail: `${healthMetrics.grossMargin.toFixed(1)}%`, color: '#10b981' },
                { label: 'Revenue Growth', score: healthMetrics.growthScore, detail: `${healthMetrics.revGrowth >= 0 ? '+' : ''}${healthMetrics.revGrowth.toFixed(1)}%`, color: '#f59e0b' },
                { label: 'Profit Status', score: healthMetrics.profitScore, detail: kpis.netProfit >= 0 ? 'Profit' : 'Loss', color: activeEntity === 'JUARA_GADAI' ? '#F68B1F' : '#8b5cf6' },
              ].map((f, i) => (
                <div key={i} className="exec-factor">
                  <span className="exec-factor-label">{f.label}</span>
                  <div className="exec-factor-bar">
                    <div className="exec-factor-fill" style={{ width: `${f.score}%`, backgroundColor: f.color }} />
                  </div>
                  <span className="exec-factor-value">{f.detail}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Revenue vs OpEx Chart */}
        <div className="exec-section">
          <div className="exec-section-title">📊 Revenue vs OpEx (6 Bulan Terakhir)</div>
          <SmartTooltip 
            insights={insightsEnabled ? analyzeChartTrend(sparklineData.revenue, sparklineData.labels, 'Revenue') : []} 
            title="Trend Revenue 6 Bulan"
          >
            <div style={{ height: '220px' }}>
              <Bar
                data={barChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: true, labels: { color: chartTheme.legendColor, font: { size: 11 } } },
                    tooltip: {
                      backgroundColor: chartTheme.tooltipBg,
                      titleColor: chartTheme.tooltipTitle,
                      bodyColor: chartTheme.tooltipBody,
                      borderColor: chartTheme.tooltipBorder,
                      borderWidth: 1,
                      callbacks: {
                        label: (ctx: any) => `${ctx.dataset.label}: ${formatRupiah(ctx.raw)}`,
                      },
                    },
                    datalabels: { display: false },
                  },
                  scales: {
                    x: { ticks: { color: chartTheme.tickColor, font: { size: 10 } }, grid: { color: chartTheme.gridColorX } },
                    y: {
                      ticks: {
                        color: chartTheme.tickColor,
                        font: { size: 10 },
                        callback: (v: any) => `${(v / 1e6).toFixed(0)} Jt`,
                      },
                      grid: { color: chartTheme.gridColorY },
                    },
                  },
                }}
              />
            </div>
          </SmartTooltip>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        {/* Top 5 Branches */}
        <div className="exec-section">
          <div className="exec-section-title">🏆 Top 5 Cabang by Revenue</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {topBranches.map((b, i) => (
              <div key={b.code} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{
                  width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '12px', fontWeight: '700',
                  backgroundColor: i === 0 ? 'rgba(245,158,11,0.2)' : i === 1 ? 'rgba(148,163,184,0.2)' : i === 2 ? 'rgba(180,83,9,0.2)' : 'var(--bg-secondary)',
                  color: i === 0 ? '#f59e0b' : i === 1 ? '#94a3b8' : i === 2 ? '#b45309' : 'var(--text-muted)',
                }}>
                  {i + 1}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>{b.name} ({b.code})</span>
                    <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--emerald-light)' }}>{formatRupiah(b.revenue)}</span>
                  </div>
                  <div style={{ height: '6px', backgroundColor: 'var(--bg-secondary)', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: '3px',
                      width: `${(b.revenue / maxBranchRev) * 100}%`,
                      background: `linear-gradient(90deg, #10b981, #3b82f6)`,
                      transition: 'width 0.6s ease',
                    }} />
                  </div>
                </div>
              </div>
            ))}
            {topBranches.length === 0 && (
              <div style={{ fontSize: '13px', color: 'var(--text-muted)', padding: '12px 0' }}>Tidak ada data cabang untuk area ini</div>
            )}
          </div>
        </div>

        {/* Top 5 Underperforming Branches */}
        <div className="exec-section">
          <div className="exec-section-title">⚠️ Top 5 Cabang Underperform</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {underperformingBranches.map((b, i) => (
              <div key={b.code} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{
                  width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '12px', fontWeight: '700',
                  backgroundColor: 'rgba(239, 68, 68, 0.15)',
                  color: '#ef4444',
                }}>
                  {i + 1}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>{b.name} ({b.code})</span>
                    <span style={{ fontSize: '12px', fontWeight: '600', color: b.netProfit >= 0 ? 'var(--emerald-light)' : '#f87171' }}>
                      {formatRupiah(b.netProfit)}
                    </span>
                  </div>
                  <div style={{ height: '6px', backgroundColor: 'var(--bg-secondary)', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: '3px',
                      width: `${(Math.abs(b.netProfit) / maxUnderperformLoss) * 100}%`,
                      background: `linear-gradient(90deg, #ef4444, #f97316)`,
                      transition: 'width 0.6s ease',
                    }} />
                  </div>
                </div>
              </div>
            ))}
            {underperformingBranches.length === 0 && (
              <div style={{ fontSize: '13px', color: 'var(--text-muted)', padding: '12px 0' }}>Tidak ada data cabang untuk area ini</div>
            )}
          </div>

          {/* Quick Nav */}
          <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px', fontWeight: '600' }}>AKSES CEPAT</div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {[
                { id: 'revenue', label: '💰 Revenue', color: '#10b981' },
                { id: 'expenses', label: '📉 Expenses', color: '#ef4444' },
                { id: 'balance', label: '⚖️ Balance', color: '#3b82f6' },
                { id: 'bep', label: '📈 BEP', color: '#f59e0b' },
              ].map(nav => (
                <button
                  key={nav.id}
                  onClick={() => onNavigate(nav.id)}
                  style={{
                    padding: '6px 14px', fontSize: '11px', fontWeight: '600',
                    border: '1px solid var(--border-light)', borderRadius: '20px',
                    backgroundColor: 'var(--bg-secondary)', color: 'var(--text-secondary)',
                    cursor: 'pointer', transition: 'all 0.15s ease', fontFamily: 'Inter, sans-serif',
                  }}
                  onMouseEnter={(e) => {
                    (e.target as HTMLButtonElement).style.borderColor = nav.color;
                    (e.target as HTMLButtonElement).style.color = nav.color;
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLButtonElement).style.borderColor = 'var(--border-light)';
                    (e.target as HTMLButtonElement).style.color = 'var(--text-secondary)';
                  }}
                >
                  {nav.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveSummaryPage;
